import { ReceiverDetail } from "@/commons/models/SendLineMessage";
import { SenderType } from "@/commons/services/Families";

/**
 * メッセージモデル
 */
export default interface Message {
  /** ログの際に利用する (YYYYMMDDhhmmssSSS) */
  datetime: number | string;
  /** 施設を示すID */
  facilityId: number;
  /** ソートキー */
  messagesSortKey: string;
  /** どの機能が使用されたかを集計するために利用する */
  functionType: FunctionType;
  /** 機能ごとに異なる差分を示すJSON */
  functionContents: FunctionContents;
  /** LineのID */
  lineId: string;
  /** メールアドレス */
  mailaddress: string;
  /** メッセージの種別 */
  messageType: MessageType;
  /** 送信種別。LINE:0、MAIL:1 */
  senderType: SenderType;
  /** メッセージを同一利用者の家族間でシェアするか否か */
  isSharedMessages: boolean | null;
  /** メッセージを受信した側のID */
  receiver: string;
  /** メッセージ一斉送信時のみ使用される、受信情報の詳細 */
  receiverDetails?: Array<ReceiverDetail>;
  /** 利用者を示すID */
  residentId: number;
  /** 利用者名 */
  residentName: string;
  /** 利用者名（ふりがな） */
  residentKanaName: string;
  /** 利用者の部屋名 */
  residentRoomName: string;
  /** 家族を示すID */
  familyId: number;
  /** 家族名 */
  familyName: string;
  /** メッセージを送信した側のID */
  sender: string;
  /** メッセージの送信時刻(YYYYMMDDhhmmssSSS) */
  sendTime: number;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 編集者 */
  writtenBy: string;
  /** 編集者のuserId */
  writerId: string;
  /** opinionのケースID */
  caseId: number;
  /**
   * メッセージ送信成功リスト
   * メッセージ送信画面からの送信メッセージでのみ使用される
   */
  successUsers?: Array<ReceiverDetail>;
  /**
   * メッセージ送信失敗リスト
   * メッセージ送信画面からの送信メッセージでのみ使用される
   */
  failedUsers?: Array<ReceiverDetail>;

  /** 画面で使用する、意見が選択されているか否か */
  selected?: boolean;
  /** 画面で使用する、一斉送信時の受信家族名を結合した文字列 */
  receivers?: string;
}

/** メッセージ種別 */
export enum MessageType {
  TEXT = "text",
  IMAGE = "image",
  BUTTONS = "buttons",
  FLEX = "flex",
  FUNCTION = "function",
  MULTI = "multi"
}

/** 機能種別 */
export enum FunctionType {
  /** 機能に関係しないメッセージ(ログにだけ残したいメッセージ) */
  MESSGE_LOG = 0,
  /** 今日の様子 */
  TODAY_STATUS = 1,
  /** 写真送付 */
  SEND_PICTURE = 2,
  /** イベント情報 */
  EVENT = 3,
  /** 面会予約 */
  RESERVATION = 4,
  /** 施設情報 */
  FACILITY = 5,
  /** 家族からの連絡 */
  OPINION = 6,
  /** メッセージ送信（家族） */
  SEND_MESSAGE = 7,
  /** メッセージ送信（admin 画面） */
  SEND_MESSAGE_ADMIN = 8,
  /** 面会予約削除 */
  DELETE_RESERVATION = 9,
  /** 家族からの連絡確認 */
  CONFIRM_OPINION = 10,
  /** ユーザ情報変更 */
  USER_MODIFY = 11,
  /** 施設関係者からの連絡*/
  OPINION_OTHER_USER = 16,
  /** 施設関係者からの連絡確認 */
  CONFIRM_OPINION_OTHER_USER = 17,
  /** メッセージ送信（施設関係者） */
  SEND_MESSAGE_OTHER_USER = 18,
  /** 安否確認メッセージ（施設関係者） */
  SAFETY_CONFIRMS_OTHER_USER = 22,
  /** 安否確認メッセージの応答（施設関係者） */
  REPLY_SAFETY_CONFIRMS_OTHER_USER = 23,
  /** 安否確認メッセージ（家族） */
  SAFETY_CONFIRMS_FAMILY = 24,
  /** 安否確認メッセージの応答（家族） */
  REPLY_SAFETY_CONFIRMS_FAMILY = 25,
  /** メッセージ送信（待機者家族） */
  SEND_WAITR_MESSAGE = 26,
  /** 画面表示しないメッセージログ */
  HIDDEN_MESSGE_LOG = 98,
  /** その他 */
  OTHER = 99
}

/** 機能種別毎の内容 */
export type FunctionContents = any;

/**
 * メッセージログ
 * FunctionTypeが1(メッセージログ)の場合にFunctionContentに格納される
 */
export interface MessageLogFunction {
  /** 内容 */
  content: string;
}

/**
 * 今日の様子
 * FunctionTypeが2(今日の様子)の場合にFunctionContentに格納される
 */
export interface TodaysStatusFunction {
  /** 画像URL */
  imageURL: string;
}

/**
 * 写真送付
 * FunctionTypeが2(写真送付)の場合にFunctionContentに格納される
 */
export interface SendPictureFunction {
  /** タイトル */
  title: string;
  /** 表示する施設内容 */
  content: string;
  /** 画像部分のURL */
  imageURL: string;
  /** URLリンクのタイトル部分 */
  linkTitle: string;
  /** リンクのURL */
  linkURL: string;
}

/**
 * イベント情報
 * FunctionTypeが3(イベント情報)の場合にFunctionContentに格納される
 */
export interface EventFunction {
  /** タイトル */
  title: string;
  /** 内容 */
  content: string;
  /** 開催場所 */
  place: string;
  /** 開催日 yyyy年mm月dd日(〇)の形で格納 */
  date: string;
  /** 開催時刻 hh:mm～hh:mmの形で格納 */
  time: string;
  /** 画像部分のURL */
  imageURL: string;
}

/**
 * 面会予約
 * FunctionTypeが4(面会予約)の場合にFunctionContentに格納される
 */
export interface ReservationFunction {
  /** タイトル */
  title: string;
  /** 表示する施設内容 */
  content: string;
  /** 画像部分のURL */
  imageURL: string;
  /** URLリンクのタイトル部分 */
  linkTitle: string;
  /** リンクのURL */
  linkURL: string;
}

/**
 * 施設情報
 * FunctionTypeが5(施設情報)の場合にFunctionContentに格納される
 */
export interface FacilityFunction {
  /** タイトル */
  title: string;
  /** 表示する施設内容 */
  content: string;
  /** 画像部分のURL */
  imageURL: string;
  /** URLリンクのタイトル部分 */
  linkTitle: string;
  /** リンクのURL */
  linkURL: string;
}

/**
 * 意見
 * FunctionTypeが6(家族からの連絡)の場合にFunctionContentに格納される
 */
export interface OpinionFunction {
  /** メッセージ送付先 */
  target: string;
  /** 完了日 yyyymmddhhmmssSSS */
  finished?: number;
  /** 匿名フラグ */
  hidden: boolean;
  /** 関係性 */
  familyRelation: string;
  /** 役割 */
  familyPosition: string;
  /** メッセージ内容 */
  content: string;
  /** 画面で使用する、完了しているか否か */
  isFinished: boolean;
}

/**
 * メッセージ送信
 * FunctionTypeが7(メッセージ送信)の場合にFunctionContentに格納される
 */
export interface SendMessageFunction {
  /** title */
  title: string;
  /** メッセージ内容 */
  content: string;
  /** 読みましたボタン追加可否 */
  addReadButton: boolean;
  /** 返信ボタン追加可否 */
  addReplyButton: boolean;
  /** 返信可能期限 */
  replyLimit: number;
  /** 強調テキスト追加可否 */
  addOptionText: boolean;
  /** 有効期限日時 */
  expirationDate?: string;
  /** 有効期限説明文 */
  expirationText?: string;
}
